import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Calendar, Plus, Trash2, Edit2, X, Save, MapPin, Video, CheckCircle2, XCircle, Clock, Filter, Mail, Download, ListChecks, CalendarRange, MessageSquare } from "lucide-react";
import AvailabilityTab from "@/pages/admin/appointments/AvailabilityTab";

const TYPE_LABELS = { in_person: "En personne", online: "En ligne" };
const STATUS_BADGES = {
  pending:   { label: "En attente",  cls: "bg-amber-100 text-amber-700",   Icon: Clock },
  confirmed: { label: "Confirme",    cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  declined:  { label: "Refuse",      cls: "bg-red-100 text-red-700",        Icon: XCircle },
  cancelled: { label: "Annule",      cls: "bg-gray-100 text-gray-500",      Icon: XCircle },
  completed: { label: "Termine",     cls: "bg-blue-100 text-blue-700",      Icon: CheckCircle2 },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function isoNoTZ(d) {
  // local datetime → ISO (no TZ to keep simple display)
  return new Date(d).toISOString().slice(0, 16);
}

export default function AppointmentsModule() {
  const [appts, setAppts] = useState([]);
  const [clients, setClients] = useState([]);
  const [filterClient, setFilterClient] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | <appt object>
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // Batch #14.152 — "list" | "availability"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterClient) params.client_id = filterClient;
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;
      const r = await api.get("/appointments", { params });
      setAppts(r.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [filterClient, filterType, filterStatus]);

  useEffect(() => {
    load();
    api.get("/clients").then(r => setClients(r.data)).catch((e) => { console.error("[Appointments] load clients failed:", e); });
  }, [load]);

  const groups = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now); todayStart.setHours(0, 0, 0, 0);
    const tomorrowEnd = new Date(todayStart); tomorrowEnd.setDate(tomorrowEnd.getDate() + 2);
    const weekEnd = new Date(todayStart); weekEnd.setDate(weekEnd.getDate() + 7);
    const result = { today: [], soon: [], later: [], past: [] };
    appts.forEach(a => {
      const t = new Date(a.start_time);
      if (t < now) result.past.push(a);
      else if (t < tomorrowEnd) result.today.push(a);
      else if (t < weekEnd) result.soon.push(a);
      else result.later.push(a);
    });
    return result;
  }, [appts]);

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer ce rendez-vous ?")) return;
    try { await api.delete(`/appointments/${id}`); toast.success("Supprime"); load(); }
    catch { toast.error("Erreur"); }
  };

  const onResend = async (id) => {
    try { await api.post(`/appointments/${id}/notify`); toast.success("Invitation renvoyee"); }
    catch { toast.error("Erreur"); }
  };
  // Batch #14.328 — texto « Répondez OUI pour confirmer » envoyé tout de suite
  const onSmsConfirm = async (id) => {
    if (!window.confirm("Envoyer maintenant le texto de confirmation (le client répond OUI ou NON) ?")) return;
    try { await api.post(`/admin/sms/automation/appointments/${id}/confirm-request`); toast.success("Texto de confirmation envoyé"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
  };

  const onIcs = async (id) => {
    try {
      const r = await api.get(`/appointments/${id}/ics`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `rdv-${id.slice(0,8)}.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="appointments-module">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-indigo-600" /> Rendez-vous
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">{appts.length} rendez-vous</p>
        </div>
        {view === "list" && (
          <button onClick={() => setEditing("new")}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2"
            data-testid="new-appt-btn">
            <Plus className="w-4 h-4" /> Nouveau rendez-vous
          </button>
        )}
      </div>

      {/* Batch #14.152 — Tabs Liste / Disponibilités */}
      <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
        <button onClick={() => setView("list")}
                className={`px-4 py-2 text-sm font-medium border-b-2 inline-flex items-center gap-1.5 transition ${
                  view === "list" ? "border-indigo-600 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
                data-testid="appt-tab-list">
          <ListChecks className="w-4 h-4" /> Liste des RDV
        </button>
        <button onClick={() => setView("availability")}
                className={`px-4 py-2 text-sm font-medium border-b-2 inline-flex items-center gap-1.5 transition ${
                  view === "availability" ? "border-indigo-600 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
                data-testid="appt-tab-availability">
          <CalendarRange className="w-4 h-4" /> Mes disponibilités
        </button>
      </div>

      {view === "availability" ? (
        <AvailabilityTab />
      ) : (
        <>
          {/* Filters */}
      <div className="bg-white border rounded-xl p-3 mb-4 flex gap-2 flex-wrap items-center" data-testid="appt-filters">
        <Filter className="w-4 h-4 text-gray-400" />
        <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
          className="border rounded-lg px-3 py-1.5 text-sm" data-testid="filter-client">
          <option value="">Tous clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="border rounded-lg px-3 py-1.5 text-sm" data-testid="filter-type">
          <option value="">Tous types</option>
          <option value="in_person">En personne</option>
          <option value="online">En ligne</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border rounded-lg px-3 py-1.5 text-sm" data-testid="filter-status">
          <option value="">Tous statuts</option>
          {Object.entries(STATUS_BADGES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {(filterClient || filterType || filterStatus) && (
          <button onClick={() => { setFilterClient(""); setFilterType(""); setFilterStatus(""); }}
            className="text-xs text-indigo-600 hover:underline">Reinitialiser</button>
        )}
      </div>

      {loading ? (
        <p className="text-center text-gray-400 py-12">Chargement...</p>
      ) : appts.length === 0 ? (
        <div className="bg-white border rounded-2xl p-12 text-center" data-testid="appt-empty">
          <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">Aucun rendez-vous. Cliquez sur "Nouveau" pour commencer.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.today.length > 0 && (
            <Group label="Aujourd'hui & demain" appts={groups.today} onEdit={setEditing} onDelete={onDelete} onResend={onResend} onIcs={onIcs} onSmsConfirm={onSmsConfirm} />
          )}
          {groups.soon.length > 0 && (
            <Group label="Cette semaine" appts={groups.soon} onEdit={setEditing} onDelete={onDelete} onResend={onResend} onIcs={onIcs} onSmsConfirm={onSmsConfirm} />
          )}
          {groups.later.length > 0 && (
            <Group label="Plus tard" appts={groups.later} onEdit={setEditing} onDelete={onDelete} onResend={onResend} onIcs={onIcs} onSmsConfirm={onSmsConfirm} />
          )}
          {groups.past.length > 0 && (
            <Group label="Passes" appts={groups.past} onEdit={setEditing} onDelete={onDelete} onResend={onResend} onIcs={onIcs} onSmsConfirm={onSmsConfirm} muted />
          )}
        </div>
      )}

      {editing && (
        <ApptForm
          appointment={editing === "new" ? null : editing}
          clients={clients}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
        </>
      )}
    </div>
  );
}

function Group({ label, appts, onEdit, onDelete, onResend, onIcs, onSmsConfirm, muted = false }) {
  return (
    <div data-testid={`group-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{label}</h2>
      <div className={`space-y-2 ${muted ? "opacity-60" : ""}`}>
        {appts.map(a => <ApptRow key={a.id} appt={a} onEdit={onEdit} onDelete={onDelete} onResend={onResend} onIcs={onIcs} onSmsConfirm={onSmsConfirm} />)}
      </div>
    </div>
  );
}

function ApptRow({ appt, onEdit, onDelete, onResend, onIcs, onSmsConfirm }) {
  const badge = STATUS_BADGES[appt.status] || STATUS_BADGES.pending;
  const TypeIcon = appt.type === "online" ? Video : MapPin;
  return (
    <div className="bg-white border rounded-xl p-3.5 flex items-start gap-3" data-testid={`appt-row-${appt.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={`text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${badge.cls}`}>
            <badge.Icon className="w-3 h-3" /> {badge.label}
          </span>
          <span className="text-sm font-semibold text-gray-800">{appt.title}</span>
          {appt.confirmed_via === "sms" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`appt-sms-reply-${appt.id}`}>{appt.status === "confirmed" ? "Confirmé par SMS" : "Annulé par SMS"}</span>}
          {!appt.confirmed_via && appt.sms_confirm_sent_at && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200" data-testid={`appt-sms-sent-${appt.id}`}>Texto OUI envoyé</span>}
        </div>
        <div className="text-xs text-gray-600 flex items-center gap-3 flex-wrap">
          <span>{appt.client_name || "—"}</span>
          <span className="text-gray-400">•</span>
          <span>{fmtDate(appt.start_time)} → {fmtDate(appt.end_time).split(" ").slice(-2).join(" ")}</span>
          <span className="text-gray-400">•</span>
          <span className="inline-flex items-center gap-1"><TypeIcon className="w-3 h-3" /> {TYPE_LABELS[appt.type]}</span>
        </div>
        {appt.location && <div className="text-xs text-gray-500 mt-1 truncate">{appt.location}</div>}
        {appt.meeting_url && (
          <a href={appt.meeting_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline mt-1 inline-block truncate max-w-md">
            {appt.meeting_url}
          </a>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <button onClick={() => onResend(appt.id)} title="Renvoyer l'invitation"
          className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-gray-50 rounded" data-testid={`appt-resend-${appt.id}`}>
          <Mail className="w-3.5 h-3.5" />
        </button>
        {appt.status === "pending" && (
          <button onClick={() => onSmsConfirm(appt.id)} title="Texto « Répondez OUI pour confirmer »"
            className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-gray-50 rounded" data-testid={`appt-sms-confirm-${appt.id}`}>
            <MessageSquare className="w-3.5 h-3.5" />
          </button>
        )}
        <button onClick={() => onIcs(appt.id)} title="Telecharger .ics"
          className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-gray-50 rounded" data-testid={`appt-ics-${appt.id}`}>
          <Download className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onEdit(appt)} title="Modifier"
          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-gray-50 rounded" data-testid={`appt-edit-${appt.id}`}>
          <Edit2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onDelete(appt.id)} title="Supprimer"
          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-gray-50 rounded" data-testid={`appt-delete-${appt.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function ApptForm({ appointment, clients, onClose, onSaved }) {
  const [data, setData] = useState(() => {
    if (appointment) {
      return {
        client_id: appointment.client_id,
        title: appointment.title,
        description: appointment.description || "",
        type: appointment.type,
        location: appointment.location || "",
        meeting_url: appointment.meeting_url || "",
        start_time: appointment.start_time?.slice(0, 16) || "",
        end_time: appointment.end_time?.slice(0, 16) || "",
        status: appointment.status || "pending",
        notes_admin: appointment.notes_admin || "",
      };
    }
    const now = new Date(); now.setMinutes(0, 0, 0); now.setHours(now.getHours() + 1);
    const end = new Date(now); end.setHours(end.getHours() + 1);
    return {
      client_id: "", title: "", description: "",
      type: "in_person", location: "", meeting_url: "",
      start_time: isoNoTZ(now), end_time: isoNoTZ(end),
      status: "pending", notes_admin: "",
    };
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!data.client_id) return toast.error("Client requis");
    if (!data.title.trim()) return toast.error("Titre requis");
    if (!data.start_time || !data.end_time) return toast.error("Plage horaire requise");
    if (data.end_time <= data.start_time) return toast.error("Fin doit etre apres debut");
    setSaving(true);
    try {
      const payload = { ...data };
      if (appointment) {
        await api.put(`/appointments/${appointment.id}`, payload);
        toast.success("Modifie");
      } else {
        await api.post("/appointments", payload);
        toast.success("Cree — invitation envoyee");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="appt-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800">
            {appointment ? "Modifier le rendez-vous" : "Nouveau rendez-vous"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Client *" className="col-span-2">
            <select value={data.client_id} onChange={e => setData({ ...data, client_id: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="appt-client" disabled={!!appointment}>
              <option value="">Selectionner...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </Field>

          <Field label="Titre *" className="col-span-2">
            <input type="text" value={data.title} onChange={e => setData({ ...data, title: e.target.value })}
              placeholder="Ex: Revue annuelle T1" className="w-full border rounded-lg px-3 py-2 text-sm"
              data-testid="appt-title" />
          </Field>

          <Field label="Debut *">
            <input type="datetime-local" value={data.start_time}
              onChange={e => setData({ ...data, start_time: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="appt-start" />
          </Field>
          <Field label="Fin *">
            <input type="datetime-local" value={data.end_time}
              onChange={e => setData({ ...data, end_time: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="appt-end" />
          </Field>

          <Field label="Type *" className="col-span-2">
            <div className="flex gap-2">
              {[
                { v: "in_person", l: "En personne", I: MapPin },
                { v: "online",    l: "En ligne",    I: Video },
              ].map(opt => {
                const active = data.type === opt.v;
                return (
                  <button key={opt.v} type="button"
                    onClick={() => setData({ ...data, type: opt.v })}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm border inline-flex items-center justify-center gap-2 transition ${
                      active ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-700 hover:border-indigo-400"
                    }`}
                    data-testid={`appt-type-${opt.v}`}>
                    <opt.I className="w-4 h-4" /> {opt.l}
                  </button>
                );
              })}
            </div>
          </Field>

          {data.type === "in_person" && (
            <Field label="Adresse / Lieu" className="col-span-2">
              <input type="text" value={data.location}
                onChange={e => setData({ ...data, location: e.target.value })}
                placeholder="412 Rue J.R Theberge, Chicoutimi"
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="appt-location" />
            </Field>
          )}
          {data.type === "online" && (
            <Field label="Lien de la reunion (Teams / Google Meet / Zoom)" className="col-span-2">
              <input type="url" value={data.meeting_url}
                onChange={e => setData({ ...data, meeting_url: e.target.value })}
                placeholder="https://meet.google.com/..."
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="appt-url" />
              <p className="text-[11px] text-gray-400 mt-1">
                Coller le lien manuellement (cree dans Teams ou Google Meet de votre cote).
              </p>
            </Field>
          )}

          <Field label="Description / Ordre du jour" className="col-span-2">
            <textarea value={data.description} rows={2}
              onChange={e => setData({ ...data, description: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="appt-description" />
          </Field>

          {appointment && (
            <Field label="Statut">
              <select value={data.status} onChange={e => setData({ ...data, status: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="appt-status">
                {Object.entries(STATUS_BADGES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
          )}

          <Field label="Notes internes (visible cabinet uniquement)" className="col-span-2">
            <textarea value={data.notes_admin} rows={2}
              onChange={e => setData({ ...data, notes_admin: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="appt-notes-admin" />
          </Field>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">Annuler</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="appt-save">
            <Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer & inviter"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
